import { useState, useEffect } from 'react';
import { Box, Grid, Icon, Typography } from '../../base/index';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
    emptyResetPasswordForm,
    IResetPasswordForm,
    ResetPasswordFormFields,
    ResetPasswordSchema,
} from '../../common/index';
import { Form, FormInputText, useAppSelector, useTheme } from '../../base';
import { loginSelectors } from '../../actions';
import {
    ResetPasswordButton,
    ResetPasswordFormWrapper,
} from './ResetPasswordForm.styles';

type RequirementKey =
    | 'minLength'
    | 'upperCase'
    | 'lowerCase'
    | 'number'
    | 'specialChar';

interface Props {
    onSubmit: (payload: { new_password: string }) => void;
    isMobile: boolean;
}

const ResetPasswordForm = ({ onSubmit, isMobile }: Props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isLoginLoading = useAppSelector(loginSelectors.getIsLoginLoading);

    const methods = useForm<IResetPasswordForm>({
        resolver: yupResolver(ResetPasswordSchema(t)),
        mode: 'onSubmit',
        defaultValues: emptyResetPasswordForm,
    });

    const enteredPassword = methods.watch(ResetPasswordFormFields.password);

    const [requirementStatuses, setRequirementStatuses] = useState<{
        [key in RequirementKey]: boolean;
    }>({
        minLength: false,
        upperCase: false,
        lowerCase: false,
        number: false,
        specialChar: false,
    });

    useEffect(() => {
        const minLength = enteredPassword.length >= 8;
        const upperCase = /[A-Z]/.test(enteredPassword);
        const lowerCase = /[a-z]/.test(enteredPassword);
        const number = /\d/.test(enteredPassword);
        const specialChar = /[!@#$%^&*(),.?":{}|<>]/.test(enteredPassword);

        setRequirementStatuses({
            minLength,
            upperCase,
            lowerCase,
            number,
            specialChar,
        });
    }, [enteredPassword]);

    const onResetPasswordSubmit = () => {
        const formData = methods.getValues();
        const payload = {
            new_password: formData[ResetPasswordFormFields.password],
        };
        onSubmit(payload);
    };

    const isEnableSavePassword = (): boolean => {
        const isRequirementMet = Object.values(requirementStatuses).every(
            (status) => status === true
        );
        const isFormValid = methods.formState.isValid;
        return isRequirementMet && isFormValid;
    };

    const passwordRequirements: { text: string; key: RequirementKey }[] = [
        {
            text: t('resetPasswordPage.minimumCharacters', {
                amountOfCharacters: 8,
            }),
            key: 'minLength',
        },
        { text: t('resetPasswordPage.upperCaseRequirement'), key: 'upperCase' },
        { text: t('resetPasswordPage.lowerCaseCharacter'), key: 'lowerCase' },
        { text: t('resetPasswordPage.aNumber'), key: 'number' },
        { text: t('resetPasswordPage.aSpecialCharacter'), key: 'specialChar' },
    ];

    const renderRequirement = (requirement: {
        text: string;
        key: RequirementKey;
    }) => (
        <Grid item xs={12} key={requirement.key} pb={1.25}>
            <Box display="flex" alignItems="center">
                <Icon
                    name={
                        requirementStatuses[requirement.key]
                            ? 'TaskAltRoundedIcon'
                            : 'ErrorOutlineRoundedIcon'
                    }
                    sx={{
                        color: requirementStatuses[requirement.key]
                            ? theme.palette.success.main
                            : theme.palette.error.main,
                    }}
                    size={20}
                />
                <Typography variant={'body2'} pl={1}>
                    {requirement.text}
                </Typography>
            </Box>
        </Grid>
    );

    return (
        <ResetPasswordFormWrapper container>
            <Form methods={methods} onSubmit={onResetPasswordSubmit}>
                <Grid
                    item
                    xs={12}
                    pt={3}
                    pb={3.25}
                    sx={{ textAlign: isMobile ? 'center' : 'left' }}
                >
                    <Typography variant={'h4'} color={'primary'}>
                        {t('resetPasswordPage.title')}
                    </Typography>
                </Grid>
                <Grid item xs={12} sx={{ textAlign: 'center' }} pb={4}>
                    <Typography variant={'body1'}>
                        {t('resetPasswordPage.description')}
                    </Typography>
                </Grid>
                <Grid
                    item
                    xs={12}
                    display={'flex'}
                    justifyContent={'center'}
                    pb={2}
                >
                    <FormInputText
                        name={ResetPasswordFormFields.password}
                        control={methods.control}
                        label={t('resetPasswordPage.newPassword')}
                        required
                        inputType={'password'}
                    />
                </Grid>
                <Grid
                    item
                    xs={12}
                    display={'flex'}
                    justifyContent={'center'}
                    pb={1}
                >
                    <FormInputText
                        name={ResetPasswordFormFields.passwordReEntry}
                        control={methods.control}
                        label={t('resetPasswordPage.reenterNewPassword')}
                        required
                        inputType={'password'}
                    />
                </Grid>
                <Grid item xs={12} pt={3} pb={1}>
                    <Typography variant={'body2'} fontWeight={'bold'}>
                        {t('resetPasswordPage.YourPasswordMustContain')}
                    </Typography>
                </Grid>
                {passwordRequirements.map(renderRequirement)}
                <Grid
                    item
                    xs={12}
                    display={'flex'}
                    justifyContent={isMobile ? 'center' : 'right'}
                    pt={2}
                >
                    <ResetPasswordButton
                        variant={'contained'}
                        type={'submit'}
                        loadingPosition={'start'}
                        startIcon={<></>}
                        loading={isLoginLoading}
                        disabled={!isEnableSavePassword()}
                        fullWidth={isMobile}
                        sx={{
                            width: isMobile ? '100%' : undefined,
                            minWidth: isMobile ? 'unset' : undefined,
                        }}
                    >
                        {t('resetPasswordPage.resetPasswordButtonText')}
                    </ResetPasswordButton>
                </Grid>
            </Form>
        </ResetPasswordFormWrapper>
    );
};

export default ResetPasswordForm;
